<template>
  <div
    class="dialog__header tw-flex tw-w-full tw-flex-col tw-border-b tw-border-gray-medium"
    :style="headerStyle"
  >
    <!-- Row 1: Title, Clear, Close -->
    <div
      class="header-row-1 tw-flex tw-w-full tw-items-center tw-justify-between tw-p-3"
      :class="{
        'tw-bg-gray-light': showRow2 && (hasSecondRow || $slots['second-row']),
      }"
    >
      <div class="tw-flex tw-min-w-0 tw-flex-1 tw-items-center">
        <span ref="titleRef" class="dialog__title tw-truncate">
          {{ title }}
          <q-tooltip
            v-if="isTitleTruncated"
            :delay="250"
            anchor="bottom middle"
            self="top middle"
          >
            {{ title }}
          </q-tooltip>
        </span>
        <span
          v-if="clearable"
          ref="clearLinkRef"
          class="dialog__clear-link tw-inline-flex tw-items-center tw-gap-2"
          :class="{
            'tw-pointer-events-none tw-opacity-40': !hasClearableContent,
          }"
          tabindex="-1"
          role="button"
          :aria-label="clearAriaLabel"
          :aria-disabled="!hasClearableContent"
          @click="hasClearableContent && handleClear()"
          @keydown.enter.prevent="hasClearableContent && handleClear()"
          @keydown.space.prevent="hasClearableContent && handleClear()"
        >
          <span class="dialog__clear-text">{{ clearText }}</span>
          <keyboard-shortcut-hint
            shortcut="c"
            theme="gray-medium"
            aria-label="Press C to clear all"
          />
        </span>
      </div>
      <p-dialog-close-icon @click="handleClose" />
    </div>

    <!-- Row 2: Controls (optional) -->
    <div
      v-if="showRow2"
      class="header-row-2 tw-w-full tw-bg-gray-light tw-px-3 tw-pb-3"
    >
      <!-- Custom second row content -->
      <slot v-if="$slots['second-row']" name="second-row" />

      <!-- Default controls (only show if no custom slot) -->
      <div v-else class="tw-flex tw-w-full tw-items-center">
        <!-- Select All Checkbox -->
        <q-checkbox
          v-if="showSelectAll"
          ref="selectAllRef"
          :model-value="selectAllState"
          true-value="all"
          false-value="none"
          indeterminate-value="some"
          size="xs"
          dense
          class="tw-mr-3 tw-shrink-0"
          @update:model-value="handleSelectAll"
        />

        <!-- Find Input -->
        <find-input
          v-if="showFind"
          ref="findInputRef"
          class="tw-flex-1"
          :model-value="searchQuery"
          :placeholder="findPlaceholder"
          :loading="loading"
          :autofocus="autoFocusFind"
          @update:model-value="handleSearch"
          @navigate-down="handleDownArrow"
          @navigate-up="handleUpArrow"
          @submit="handleEnterKey"
        />

        <!-- Custom controls slot (inserted after FindInput, before Badge) -->
        <slot name="header-controls" />

        <!-- Badge Count -->
        <span
          v-if="showBadgeCount && badgeCount > 0"
          class="selector__count-badge tw-ml-3 tw-text-white"
          :class="badgeColorClass"
          :aria-label="`${badgeCount} selected`"
        >
          {{ formattedBadgeCount }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick, watch, useSlots } from 'vue';
import type { QCheckbox } from 'quasar';
import { Colors } from '@/api';
import type { SelectAllState } from '../types';
import FindInput from './FindInput.vue';
import KeyboardShortcutHint from '@/components/core/KeyboardShortcutHint.vue';

// Export the config interface for external use
export interface SharedModalHeaderConfig {
  // Display
  title: string;
  primaryColor?: Colors;

  // Controls
  clearable?: boolean;
  clearText?: string;
  clearAriaLabel?: string;
  hasClearableContent?: boolean;

  // Find
  showFind?: boolean;
  findPlaceholder?: string;
  autoFocusFind?: boolean;

  // Select All
  showSelectAll?: boolean;
  selectAllState?: SelectAllState;

  // Badge
  showBadgeCount?: boolean;
  badgeCount?: number;

  // State
  searchQuery?: string;
  loading?: boolean;

  // Row Visibility Control
  showRow2?: boolean;
}

interface Props {
  config: SharedModalHeaderConfig;
}

interface Emits {
  (e: 'clear'): void;
  (e: 'close'): void;
  (e: 'search', query: string): void;
  (e: 'search-submit', query: string): void;
  (e: 'select-all', value: boolean): void;
  (e: 'focus-first'): void;
  (e: 'focus-last'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const slots = useSlots();

// Refs
const titleRef = ref<HTMLElement>();
const clearLinkRef = ref<HTMLElement>();
const findInputRef = ref<InstanceType<typeof FindInput>>();
const selectAllRef = ref<InstanceType<typeof QCheckbox>>();

// Title truncation detection
const isTitleTruncated = ref(false);

function checkTitleTruncation() {
  if (titleRef.value) {
    isTitleTruncated.value =
      titleRef.value.scrollWidth > titleRef.value.clientWidth;
  }
}

// Theme colors - using CSS variables directly

// Extract values from config with defaults
const title = computed(() => props.config.title || '');
const primaryColor = computed(() => props.config.primaryColor || Colors.BLUE);
const clearable = computed(() => props.config.clearable ?? false);
const clearText = computed(() => props.config.clearText || 'Clear');
const clearAriaLabel = computed(
  () => props.config.clearAriaLabel || 'Clear all selections'
);
const hasClearableContent = computed(
  () => props.config.hasClearableContent ?? true
);
const showFind = computed(() => props.config.showFind || false);
const findPlaceholder = computed(() => props.config.findPlaceholder || 'Find');
const autoFocusFind = computed(() => props.config.autoFocusFind ?? true);
const showSelectAll = computed(() => props.config.showSelectAll || false);
const selectAllState = computed(() => props.config.selectAllState || 'none');
const showBadgeCount = computed(() => props.config.showBadgeCount || false);
const badgeCount = computed(() => props.config.badgeCount || 0);
const searchQuery = computed(() => props.config.searchQuery || '');
const loading = computed(() => props.config.loading || false);

// Row visibility computed properties
const showRow2 = computed(() => props.config.showRow2 ?? true); // Default to true for backward compatibility

// Computed
const headerStyle = computed(() => {
  return {
    '--primary-color': `rgb(var(--tw-color-${primaryColor.value}))`,
  };
});

const hasSecondRow = computed(() => {
  return (
    showFind.value ||
    showSelectAll.value ||
    showBadgeCount.value ||
    !!slots['second-row']
  );
});

const formattedBadgeCount = computed(() => {
  return badgeCount.value.toString();
});

const badgeColorClass = computed(() => {
  // Use primary color or default to blue
  const colorMap: Record<string, string> = {
    blue: 'tw-bg-blue',
    green: 'tw-bg-green',
    red: 'tw-bg-red',
    yellow: 'tw-bg-yellow',
    purple: 'tw-bg-purple',
    orange: 'tw-bg-orange',
    teal: 'tw-bg-teal',
    pink: 'tw-bg-pink',
    gray: 'tw-bg-gray-medium',
  };

  return colorMap[primaryColor.value?.toLowerCase() || 'blue'] || 'tw-bg-blue';
});

// Methods
function handleClear() {
  emit('clear');
}

function handleClose() {
  emit('close');
}

function handleSearch(value: string | number | null) {
  emit('search', String(value || ''));
}

function handleSelectAll(value: SelectAllState | boolean | null) {
  emit('select-all', value === 'all' || value === true);
}

function handleDownArrow() {
  emit('focus-first');
}

function handleUpArrow() {
  emit('focus-last');
}

function handleEnterKey(value: string) {
  emit('search-submit', value);
}

// Focus management
async function focusFind() {
  await nextTick();
  // Add a retry mechanism to ensure focus is set even if the DOM isn't ready
  const attemptFocus = (retries = 3) => {
    if (findInputRef.value?.focus) {
      findInputRef.value.focus();
      // Verify focus was actually set
      setTimeout(() => {
        const inputElement =
          findInputRef.value?.inputRef?.$el?.querySelector('input');
        if (
          inputElement &&
          document.activeElement !== inputElement &&
          retries > 0
        ) {
          // Focus wasn't set properly, retry
          attemptFocus(retries - 1);
        }
      }, 50);
    } else if (retries > 0) {
      // Component not ready yet, retry
      setTimeout(() => attemptFocus(retries - 1), 50);
    }
  };
  attemptFocus();
}

async function focusClear() {
  await nextTick();
  clearLinkRef.value?.focus();
}

// Auto-focus find input when it becomes visible
watch(
  () => showFind.value,
  async (show) => {
    if (show && autoFocusFind.value) {
      await nextTick();
      focusFind();
    }
  }
);

// Initial focus and truncation check
onMounted(async () => {
  if (showFind.value && autoFocusFind.value) {
    await focusFind();
  }
  // Check truncation after DOM is ready
  await nextTick();
  checkTitleTruncation();
});

// Re-check truncation when title changes
watch(title, async () => {
  await nextTick();
  checkTitleTruncation();
});

// Expose methods
defineExpose({
  focusFind,
  focusClear,
  findInputRef,
  clearLinkRef,
  selectAllRef,
});
</script>

<style lang="scss" scoped>
.dialog__header {
  padding: 0;
}

.dialog__clear-link {
  color: var(--primary-color);
  @apply tw-ml-3 tw-text-sm tw-cursor-pointer tw-select-none;

  &:hover .dialog__clear-text {
    @apply tw-underline;
  }

  &:focus {
    outline: 1px solid var(--primary-color);
    outline-offset: 1px;
  }

  &:active {
    color: var(--primary-color);
    opacity: 0.8;
  }
}

// Badge styles
.selector__count-badge {
  @apply tw-min-w-[20px] tw-h-5 tw-px-1;
  @apply tw-rounded-full tw-text-xs tw-font-bold;
  @apply tw-inline-flex tw-items-center tw-justify-center;
  @apply tw-select-none tw-shrink-0;

  // Ensure minimum size for single digits
  min-width: 1.25rem;
}

// Input overrides for consistent sizing
:deep(.input-xs) {
  .q-field__control {
    height: 32px;
    background: transparent;

    input {
      @apply tw-text-sm;
    }
  }

  .q-field__control-container {
    border-top: none;
    border-bottom: none;
  }

  .q-field__append {
    @apply tw-text-gray-medium;
  }
}

// Focus styles for Find input
:deep(.q-input) {
  // Keep default gray-medium border when not focused
  &.q-field--outlined .q-field__control:before {
    @apply tw-border-gray-medium;
  }

  // Only apply primary color when focused
  &.q-field--focused {
    .q-field__control {
      &:before {
        border-color: var(--primary-color);
        border-width: 1px;
      }

      &:after {
        display: none; // Hide Quasar's default focus indicator
      }
    }

    .q-field__control:hover:before {
      border-color: var(--primary-color);
    }
  }

  // Remove focus from the input element itself
  input {
    &:focus {
      outline: none;
      box-shadow: none;
    }
  }
}

// Focus styles for Select All checkbox
:deep(.q-checkbox) {
  // Target the actual focusable element
  input[type='checkbox'] {
    &:focus + .q-checkbox__inner {
      border: 1px solid var(--primary-color);
      outline: none;
    }
  }
}
</style>
